import LoaderAnimation from "@/assets/animations/Loader.json";
import { useGetAppSettingsQuery } from "@invesco/shared/api/appSettings";
import { Overlay, useTheme } from "@rneui/themed";
import Lottie from "lottie-react";
import LottieView from "lottie-react-native";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Platform, StatusBar, StyleSheet, Text, View } from "react-native";

const HIDE_DEBOUNCE_MS = 300;

type ScreenLoaderProps = {
  /** When provided, controls visibility with debounced hide to prevent flicker from rapid loading toggles. Omit for always-visible (e.g. Suspense fallback). */
  visible?: boolean;
};

const ScreenLoader = ({ visible = true }: ScreenLoaderProps) => {
  const { theme } = useTheme();
  const [debouncedVisible, setDebouncedVisible] = useState(visible);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dynamic promo text shown below the loader (from the public app-settings API).
  // RTK Query caches this globally, so repeated loaders don't refetch.
  const { data: appSettings } = useGetAppSettingsQuery();
  const loaderMessage = appSettings?.loaderMessages?.trim();

  useEffect(() => {
    if (visible) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setDebouncedVisible(true);
      if (Platform.OS !== "web") {
        StatusBar.setHidden(false, "fade");
      }
    } else {
      hideTimeoutRef.current = setTimeout(() => {
        setDebouncedVisible(false);
        if (Platform.OS !== "web") {
          StatusBar.setHidden(false, "fade");
        }
        hideTimeoutRef.current = null;
      }, HIDE_DEBOUNCE_MS);
    }
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      if (Platform.OS !== "web") {
        StatusBar.setHidden(false, "fade");
      }
    };
  }, [visible]);

  if (!debouncedVisible) return null;

  // On web: use a fixed full-screen overlay via absoluteFillObject
  if (Platform.OS === "web") {
    return (
      <Overlay
        isVisible={debouncedVisible}
        backdropStyle={{
          backgroundColor: theme.colors.white,
        }}
        overlayStyle={{
          backgroundColor: theme.colors.white,
          padding: 0,
          margin: 0,
          borderRadius: 0,
          boxShadow: "none",
        }}
      >
        {/* ✅ ✅ ACCESSIBILITY FIX */}
        <div
          role="alert"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            clip: "rect(1px, 1px, 1px, 1px)",
          }}
        >
          {debouncedVisible ? `Processing your request, please wait` : ""}
        </div>

        <View style={styles.webContainer}>
          <View style={styles.animationWrapper}>
            <Lottie
              animationData={LoaderAnimation}
              autoPlay
              loop
              style={{
                width: 700,
                height: 700,
                backgroundColor: "transparent",
              }}
            />
          </View>
          {!!loaderMessage && (
            <Text style={[styles.loaderMessage, { color: theme.colors.primary }]}>
              {loaderMessage}
            </Text>
          )}
        </View>
      </Overlay>
    );
  }

  // On native: Modal renders at the OS window level, completely outside the
  // React component tree / parent containers — no flicker, no positional shifts.
  return (
    <Modal
      visible={debouncedVisible}
      transparent={false}
      animationType="none"
      statusBarTranslucent
      hardwareAccelerated
    >
      <View style={[styles.overlay, { backgroundColor: theme.colors.white }]}>
        <View style={styles.animationWrapper}>
          <LottieView source={LoaderAnimation} autoPlay loop style={styles.lottie} />
        </View>
        {!!loaderMessage && (
          <Text style={[styles.loaderMessage, { color: theme.colors.primary }]}>
            {loaderMessage}
          </Text>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  webContainer: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: 0,
  },
  animationWrapper: {
    // Constrain the oversized 700×700 Lottie frame to a smaller visible area.
    // Overflow hidden clips the animation; the wrapper is intentionally smaller than the Lottie.
    width: 200,
    height: 140,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  lottie: {
    width: 700,
    height: 700,
  },
  loaderMessage: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
    letterSpacing: 0.3,
    paddingHorizontal: 24,
  },
});

export default ScreenLoader;
